


















































































import { Component, Prop, Vue } from "vue-property-decorator";
import { namespace, State } from "vuex-class";
import { BusinessRulesState } from "@/models/business-rules/BusinessRulesState";
import { CreateGroupPayload } from "@/models/business-rules/CreateGroupPayload";
import { GroupData } from "@/models/business-rules/GroupData";
import { RulesConditions } from "@/models/business-rules/RulesConditions";
import { RulesOutcomePayload } from "@/models/business-rules/RulesOutcomePayload";
import { RulesSetData } from "@/models/business-rules/RulesSetData";
import { RulesSetPayload } from "@/models/business-rules/RulesSetPayload";
import ApplicationModule from "@/store/modules/application";
import BusinessRulesModule from "@/store/modules/business-rules";
import ApplicationModal from "@/components/global/ApplicationModal.vue";

const application = namespace(ApplicationModule.name);
const businessRules = namespace(BusinessRulesModule.name);

@Component({
  name: "RuleEditorFooter",
  components: { ApplicationModal }
})

export default class RuleEditorFooter extends Vue {
  // ===== Props ===== //
  @Prop(String) private readonly flow?: string;

  // ===== Store ===== //
  @application.Action("getFacilities") public getFacilities!: () => void;
  @State("businessRules") private readonly businessRules!: BusinessRulesState;
  @businessRules.Action("createRuleSet") public createRuleSet!: (payload: RulesSetPayload) => void;
  @businessRules.Action("deleteGroup") public deleteGroup!: (id: number) => void;
  @businessRules.Action("deleteIndividualRuleSet") public deleteIndividualRuleSet!: (ruleset: RulesSetData) => void;
  @businessRules.Action("handleCurrentStep") public handleCurrentStep!: (step: number) => void;
  @businessRules.Action("handleGroupSaved") public handleGroupSaved!: (status: boolean) => void;
  @businessRules.Action("handleResetBusinessRules") public handleResetBusinessRules!: () => void;
  @businessRules.Action("updateGroup") public updateGroup!: (payload: GroupData) => void;
  @businessRules.Action("updateIndividualRuleSet") public updateIndividualRuleSet!: (payload: {}) => void;

  // ===== Data ===== //
  public deleteModal = false;
  public deleteStatus = false;
  public loading = false;

  // ===== Methods ===== //
  public async handleConfirmDelete(): Promise<void> {
    this.deleteStatus = true;
    if (this.groupFlow) {
      await this.deleteGroup(this.businessRules.selectedGroup!.id);
      this.getFacilities();
      this.handleCurrentStep(0);
    } else if (this.businessRules.selectedRuleSet) {
      await this.deleteIndividualRuleSet(this.businessRules.selectedRuleSet!);
      this.handleCurrentStep(2);
    } else {
      this.handleResetBusinessRules();
      this.handleCurrentStep(0);
    }
    this.deleteStatus = false;
    this.deleteModal = false;
  }
  public handleCloseDeleteModal(): void {
    this.deleteModal = false;
  }
  public handleDeleteModal(): void {
    this.deleteModal = true;
  }
  public handleReturnHome(): void {
    this.handleCurrentStep(0);
    this.handleResetBusinessRules();
  }
  public async handleSave(): Promise<void> {
    this.loading = true;
    const groupPayload = {
      id: this.businessRules.selectedGroup!.id,
      name: this.groupName,
      description: this.groupDesc,
      facilities: this.filteredFacilities
    };

    const createRuleSetPayload = {
      bre_group_id: this.businessRules.selectedGroup!.id,
      name: this.businessRules.ruleName,
      order: this.businessRules.selectedGroup!.rulesets!.length + 1,
      conditions: this.businessRules.currentSelectedConditions,
      number_days: this.businessRules.number_days,
      outcome: {
        action_metric: this.rulesOutcome!.action_metric,
        metric_comparison: this.rulesOutcome!.metric_comparison,
        metric_1: this.rulesOutcome ? this.rulesOutcome.metric_1 : "",
        metric_2: this.rulesOutcome ? this.rulesOutcome.metric_2 : null
      }
    };

    const updatedRuleSetPayload = {
      bre_group_id: this.selectedRuleSet?.bre_group_id,
      id: this.selectedRuleSet ? this.selectedRuleSet!.id : null,
      name: this.businessRules.ruleName,
      order: this.selectedRuleSet ? this.selectedRuleSet!.order : 0,
      conditions: this.businessRules.currentSelectedConditions,
      number_days: this.businessRules.number_days,
      outcome: {
        action_metric: this.rulesOutcome!.action_metric,
        metric_comparison: this.rulesOutcome!.metric_comparison,
        metric_1: this.rulesOutcome ? this.rulesOutcome.metric_1 : "",
        metric_2: this.rulesOutcome ? this.rulesOutcome.metric_2 : null
      }
    };

    if (this.groupFlow) {
      await this.updateGroup(groupPayload);
      this.getFacilities();
      this.handleGroupSaved(true);
    } else if (this.updateRuleSetValid && this.rulesFlow) {
      await this.updateIndividualRuleSet(updatedRuleSetPayload);
      this.handleCurrentStep(0);
    } else {
      await this.createRuleSet(createRuleSetPayload);
      this.handleCurrentStep(0);
    }
    this.loading = false;
  }

  // ===== Computed ===== //
  get createGroup(): CreateGroupPayload {
    return this.businessRules.createGroup;
  }
  get currentSelectedConditions(): RulesConditions[] {
    return this.businessRules.currentSelectedConditions;
  }
  get filteredFacilities() {
    return this.businessRules.selectedFacilities
      ? this.businessRules.selectedFacilities.map(facility => facility.id)
      : [];
  }
  get groupDesc(): string {
    return this.createGroup.description
      ? this.createGroup.description : this.selectedGroup!.description;
  }
  get groupFlow(): boolean {
    return this.flow === "group";
  }
  get groupName(): string {
    return this.createGroup.name
      ? this.createGroup.name : this.selectedGroup!.name;
  }
  get completedConditions(): boolean {
    if (this.groupFlow) { return true; };
    if (this.rulesFlow) {
      return !!(this.currentSelectedConditions.every(condition => condition.metric) &&
      this.currentSelectedConditions.every(condition => condition.comparison) &&
      this.currentSelectedConditions.every(condition => condition.value !== null) &&
      this.rulesOutcome.action_metric &&
      this.rulesOutcome.metric_comparison &&
      this.rulesOutcome.metric_1 &&
      this.metricTwoValid);
    }

    return false;
  }
  get metricTwoValid(): boolean | string | null {
    if (this.rulesFlow && this.rulesOutcome.metric_comparison === "equal_to") {
      return true;
    } else {
      return this.rulesOutcome.metric_2;
    }
  }
  get modalTitle(): string {
    return this.groupFlow ? "Group" : "Rule Set";
  }
  get rulesFlow(): boolean {
    return this.flow === "rules";
  }
  get rulesOutcome(): RulesOutcomePayload {
    return this.businessRules.rulesOutcome;
  }
  get selectedGroup(): GroupData | null {
    return this.businessRules.selectedGroup;
  }
  get selectedRuleSet(): RulesSetData | null {
    return this.businessRules.selectedRuleSet;
  }
  get updateRuleSetValid(): boolean {
    return !!(this.businessRules.selectedRuleSet &&
      this.rulesOutcome.bre_ruleset_id === this.businessRules.selectedRuleSet.id);
  }
}

